<script>
  // Zuordnen: Die Aufgaben einer abgeschlossenen Team-Umfrage werden L4 unter vorhandenen L3 des Projekt-Frameworks.
  // Pax AI schlaegt nur vor (vorbelegt, aenderbar); jede Aufgabe entscheidet ein Mensch, erst dann wird uebernommen -
  // ueber denselben Speicherweg wie der Framework Assistant (/multiple-project-presets, nach Namen).
  import { createEventDispatcher, onMount } from "svelte";
  import { Tooltip, Button } from "paxray-components-svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";

  export let surveyId;

  const dispatch = createEventDispatcher();
  let loading = true;
  let suggesting = false;
  let saving = false;
  let error = "";
  let paths = [];
  let branches = [];
  let tasks = [];
  let choices = {}; // key -> { value: "" | "p:<i>" | "n:<i>" | "skip", l4, newL3, source: "" | "high" | "low", note }
  let order = [];
  let added = []; // hier angelegte L2 (ggf. mit neuer L1) - gespeichert wird sie erst mit einer Aufgabe darunter
  let adding = null; // offenes Anlegen in der Zeile: { key, department, l1, newL1, l2 }

  $: groups = branches.map((branch, index) => ({
    index,
    label:
      [departmentCount > 1 ? branch.department : "", branch.l1, branch.l2]
        .filter(Boolean)
        .join(" › ") + (branch.added ? " (new)" : ""),
    paths: paths
      .map((path, pathIndex) => ({ ...path, index: pathIndex }))
      .filter(
        (path) =>
          path.department === branch.department &&
          path.l1 === branch.l1 &&
          path.l2 === branch.l2,
      ),
  }));
  $: departments = [...new Set(branches.map((branch) => branch.department))];
  $: departmentCount = departments.length;
  $: addL1Options = adding
    ? [
        ...new Set(
          branches
            .filter((branch) => branch.department === adding.department)
            .map((branch) => branch.l1),
        ),
      ]
    : [];
  $: addReady =
    adding && adding.l2.trim() && (adding.l1 || adding.newL1.trim());
  $: undecided = tasks.filter((task) => !decided(choices[task.key])).length;
  $: toAdd = tasks.filter(
    (task) =>
      choices[task.key]?.value.match(/^[pn]:/) && !choices[task.key].exists,
  ).length;

  async function request(method = "GET") {
    const response = await fetchWithSession(
      `/api/damprojects/team-surveys/${surveyId}/mapping`,
      { method },
    );
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.message || "The request failed.");
    return result;
  }

  function decided(choice) {
    if (!choice || !choice.value) return false;
    if (choice.value === "skip") return true;
    return (
      Boolean(choice.l4.trim()) &&
      (!choice.value.startsWith("n:") || Boolean(choice.newL3.trim()))
    );
  }

  function fromSuggestion(task, suggestion) {
    const target = suggestion?.target || { type: "open" };
    const base = {
      l4: suggestion?.l4 || task.name,
      newL3: "",
      source: suggestion && target.type !== "open" ? suggestion.confidence : "",
      note: "",
    };
    if (target.type === "l3") return { ...base, value: `p:${target.path}` };
    if (target.type === "new_l3")
      return { ...base, value: `n:${target.branch}`, newL3: target.name };
    // Schon vorhanden: die L3 zeigen, in der sie steht - uebertragen wird sie nicht noch einmal
    if (target.type === "duplicate")
      return {
        ...base,
        value: `p:${target.path}`,
        l4: target.name,
        exists: true,
      };
    // Ohne Vorschlag: die L2, unter der die Umfrage die Aufgabe erfasst hat - die L3 waehlt oder benennt ein Mensch
    if (Number.isInteger(task.branch))
      return { ...base, value: `n:${task.branch}`, source: "area" };
    return { ...base, value: "" };
  }

  // Offene und unsichere zuerst - einmal beim Laden, damit die Zeilen beim Bearbeiten nicht springen
  function apply(result) {
    paths = result.paths;
    branches = [...result.branches, ...added]; // angehaengt, damit die Indizes der Vorschlaege gueltig bleiben
    tasks = result.tasks;
    const byKey = Object.fromEntries(
      (result.suggestions || []).map((item) => [item.key, item]),
    );
    choices = Object.fromEntries(
      tasks.map((task) => [task.key, fromSuggestion(task, byKey[task.key])]),
    );
    const rank = (key) =>
      !choices[key].value
        ? 0
        : ["low", "area"].includes(choices[key].source)
        ? 1
        : 2;
    order = tasks.map((task) => task.key).sort((a, b) => rank(a) - rank(b));
  }

  async function load() {
    try {
      apply(await request());
    } catch (loadError) {
      error = loadError.message;
    } finally {
      loading = false;
    }
  }

  async function suggest() {
    suggesting = true;
    error = "";
    try {
      apply(await request("POST"));
    } catch {
      error =
        "Pax AI could not make suggestions. Please choose the tasks (L3) yourself.";
    } finally {
      suggesting = false;
    }
  }

  function changed(key) {
    choices = {
      ...choices,
      [key]: { ...choices[key], source: "", note: "", exists: false },
    };
  }

  function selectChanged(key) {
    if (choices[key].value !== "add") return changed(key);
    choices = { ...choices, [key]: { ...choices[key], value: "" } };
    changed(key);
    const department = departments[0];
    adding = {
      key,
      department,
      l1: branches.find((branch) => branch.department === department)?.l1 || "",
      newL1: "",
      l2: "",
    };
  }

  // Gleiche L1/L2 nicht doppelt: vorhandene wird einfach gewaehlt (Schreibweise wie im Framework)
  function addBranch() {
    const newL1 = adding.newL1.trim();
    const l1 =
      adding.l1 ||
      addL1Options.find((name) => name.toLowerCase() === newL1.toLowerCase()) ||
      newL1;
    const branch = {
      department: adding.department,
      l1,
      l2: adding.l2.trim(),
      added: true,
    };
    let index = branches.findIndex(
      (item) =>
        item.department === branch.department &&
        item.l1.toLowerCase() === branch.l1.toLowerCase() &&
        item.l2.toLowerCase() === branch.l2.toLowerCase(),
    );
    if (index < 0) {
      added = [...added, branch];
      branches = [...branches, branch];
      index = branches.length - 1;
    }
    choices = {
      ...choices,
      [adding.key]: { ...choices[adding.key], value: `n:${index}` },
    };
    adding = null;
  }

  async function save() {
    saving = true;
    error = "";
    try {
      const rows = {};
      tasks.forEach((task) => {
        const choice = choices[task.key];
        const [kind, index] = choice.value.split(":");
        if ((kind !== "p" && kind !== "n") || choice.exists) return;
        const place = kind === "p" ? paths[index] : branches[index];
        if (!rows[place.department]) rows[place.department] = [];
        rows[place.department].push({
          process_l1: place.l1,
          subprocess_l2: place.l2,
          task_l3: kind === "p" ? place.l3 : choice.newL3.trim(),
          subtask_l4: choice.l4.trim(),
        });
      });
      for (const [department, departmentRows] of Object.entries(rows)) {
        const response = await fetchWithSession(
          "/api/damprojects/multiple-project-presets",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              rows: departmentRows,
              departments: [department],
            }),
          },
        );
        if (!response.ok) throw new Error("The framework could not be saved.");
      }
      dispatch("saved", { count: toAdd });
    } catch (saveError) {
      error = saveError.message;
    } finally {
      saving = false;
    }
  }

  // L1 und L2 folgen aus der gewaehlten L3 (vorhandene L3 oder neue L3 unter einer L2)
  function place(choice) {
    const [kind, index] = (choice?.value || "").split(":");
    return kind === "p" ? paths[index] : kind === "n" ? branches[index] : null;
  }

  function facts(task) {
    return (
      [task.frequency, task.digital, task.continuity, task.standardization]
        .filter(Boolean)
        .join(" · ") + (task.share ? ` · ${task.share} %` : "")
    );
  }

  onMount(load);
</script>

<div class="mapping">
  <header>
    <h4>Transfer to your framework</h4>
    <Tooltip
      text={"Each task from the survey becomes a subtask (L4).\nChoose the task (L3) it belongs to.\nProcess (L1) and subprocess (L2) follow from it.\nTasks start under the subprocess (L2) they were listed under in the survey.\nIf none fits, add a new subprocess (L2) in the list.\nOn request, Pax AI suggests the task (L3) for each one.\nYou check every one before transferring."}
    />
    <!-- LLM nur auf Klick, nie automatisch -->
    {#if !loading}
      <span class="suggest">
        <Button
          text={suggesting ? "Pax AI is suggesting …" : "Assign with Pax AI"}
          kind="secondary"
          size="field"
          disabled={suggesting || saving}
          on:click={suggest}
        />
      </span>
    {/if}
    <button type="button" class="link" on:click={() => dispatch("close")}
      >Close</button
    >
  </header>

  {#if error}<p class="mapping-error">{error}</p>{/if}

  {#if loading || suggesting}
    <p class="muted">
      {suggesting
        ? "Pax AI is assigning the tasks … (about 10 to 20 s)"
        : "Loading …"}
    </p>
  {:else}
    <table>
      <thead>
        <tr>
          <th class="task-col">Task from the survey</th><th class="level-col"
            >Process (L1)</th
          ><th class="level-col">Subprocess (L2)</th>
          <th>Task (L3)</th><th>Subtask (L4)</th>
        </tr>
      </thead>
      <tbody>
        {#each order as key (key)}
          {@const task = tasks.find((item) => item.key === key)}
          {@const choice = choices[key]}
          {@const target = place(choice)}
          <tr class:open={!decided(choice)}>
            <td>
              <strong>{task.name}</strong>
              {#if task.description}<p>{task.description}</p>{/if}
              <p class="muted">{facts(task)}</p>
            </td>
            <td class="level">{target?.l1 || ""}</td>
            <td class="level">{target?.l2 || ""}</td>
            <td>
              <select
                bind:value={choices[key].value}
                on:change={() => selectChanged(key)}
                disabled={saving}
              >
                <option value="">Choose a task (L3)</option>
                {#each groups as group}
                  <optgroup label={group.label}>
                    {#each group.paths as path}<option value={`p:${path.index}`}
                        >{path.l3}</option
                      >{/each}
                    <option value={`n:${group.index}`}>+ New task (L3)</option>
                  </optgroup>
                {/each}
                <option value="add">+ New subprocess (L2)</option>
                <option value="skip"
                  >Don't add (already there or not needed)</option
                >
              </select>
              {#if choice.value.startsWith("n:")}
                <input
                  bind:value={choices[key].newL3}
                  maxlength="120"
                  placeholder="Name of the new task (L3)"
                  disabled={saving}
                />
              {/if}
              {#if choice.source === "high"}<span class="tag pax"
                  >Pax AI suggestion</span
                >
              {:else if choice.source === "low"}<span class="tag check"
                  >Pax AI: please check</span
                >
              {:else if choice.source === "area"}<span class="tag area"
                  >Area from the survey</span
                >{/if}
              {#if choice.exists}<span class="tag exists"
                  >Already in the framework</span
                >{/if}
              {#if choice.note}<span class="muted"> {choice.note}</span>{/if}
            </td>
            <td>
              <input
                bind:value={choices[key].l4}
                maxlength="120"
                disabled={saving || choice.value === "skip" || choice.exists}
              />
            </td>
          </tr>
          {#if adding?.key === key}
            <tr class="add-row">
              <td colspan="5">
                <div class="add-branch">
                  {#if departmentCount > 1}
                    <select
                      bind:value={adding.department}
                      on:change={() =>
                        (adding.l1 =
                          branches.find(
                            (branch) => branch.department === adding.department,
                          )?.l1 || "")}
                    >
                      {#each departments as department}<option
                          value={department}>{department}</option
                        >{/each}
                    </select>
                  {/if}
                  <select bind:value={adding.l1}>
                    {#each addL1Options as l1}<option value={l1}>{l1}</option
                      >{/each}
                    <option value="">+ New process (L1)</option>
                  </select>
                  {#if !adding.l1}<input
                      bind:value={adding.newL1}
                      maxlength="120"
                      placeholder="Name of the new process (L1)"
                    />{/if}
                  <input
                    bind:value={adding.l2}
                    maxlength="120"
                    placeholder="Name of the new subprocess (L2)"
                  />
                  <Button
                    text="Add"
                    kind="secondary"
                    size="field"
                    disabled={!addReady}
                    on:click={addBranch}
                  />
                  <button
                    type="button"
                    class="link"
                    on:click={() => (adding = null)}>Cancel</button
                  >
                </div>
              </td>
            </tr>
          {/if}
        {/each}
      </tbody>
    </table>

    <footer>
      {#if undecided}<span class="muted"
          >{undecided} still open. Decide for every task.</span
        >{/if}
      <Button
        text={saving
          ? "Saving …"
          : `Transfer ${toAdd} ${
              toAdd === 1 ? "task" : "tasks"
            } to the framework`}
        kind="secondary"
        size="field"
        disabled={saving || suggesting || undecided > 0 || !toAdd}
        on:click={save}
      />
    </footer>
  {/if}
</div>

<style>
  .mapping {
    display: grid;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border: 1px solid #dfe3e8;
    border-radius: 0.5rem;
    background: #fafafa;
  }
  header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  header > .suggest {
    margin-left: auto;
  }
  header > .link {
    margin-left: 0.5rem;
  }
  h4 {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 600;
  }
  .mapping-error {
    margin: 0;
    padding: 0.4rem 0.6rem;
    border-radius: 0.4rem;
    background: var(--pax-red);
    font-size: 0.8rem;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    font-size: 0.8rem;
  }
  th {
    padding: 0.4rem 0.5rem;
    border-bottom: 1px solid #dfe3e8;
    color: #59616c;
    font-weight: 600;
    text-align: left;
  }
  td {
    padding: 0.5rem;
    border-bottom: 1px solid var(--pax-smoke);
    vertical-align: top;
  }
  td p {
    margin: 0.15rem 0 0;
  }
  .task-col {
    width: 28%;
  }
  .level-col {
    width: 13%;
  }
  td.level {
    padding-top: 0.9rem;
    color: #59616c;
    overflow-wrap: anywhere;
  } /* auf Hoehe des Auswahlfelds */
  tr.open td:first-child {
    box-shadow: inset 3px 0 0 var(--pax-orange);
  }
  select,
  input {
    box-sizing: border-box;
    width: 100%;
    height: 2.25rem;
    padding: 0 0.5rem;
    border: 1px solid #cfd4da;
    border-radius: 0.4rem;
    background: var(--pax-white);
    font: inherit;
  }
  input {
    margin-top: 0.35rem;
  }
  td:last-child input {
    margin-top: 0;
  }
  .tag {
    display: inline-block;
    margin-top: 0.35rem;
    padding: 0.1rem 0.5rem;
    border-radius: 1rem;
    font-size: 0.72rem;
    font-weight: 600;
  }
  .tag.pax {
    background: var(--pax-green);
  }
  .tag.check {
    background: var(--pax-yellow);
  }
  .tag.area {
    border: 1px solid #cfd4da;
    background: var(--pax-white);
  }
  .tag.exists {
    margin-left: 0.25rem;
    border: 1px solid #cfd4da;
    background: var(--pax-white);
  }
  .muted {
    color: #59616c;
    font-size: 0.8rem;
  }
  .link {
    padding: 0;
    border: 0;
    background: none;
    color: var(--pax-black);
    font: inherit;
    font-size: 0.8rem;
    text-decoration: underline;
    cursor: pointer;
  }
  tr.add-row td {
    padding-top: 0;
    background: var(--pax-white);
  }
  .add-branch {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  }
  .add-branch select,
  .add-branch input {
    width: auto;
    flex: 1 1 10rem;
    margin-top: 0;
  }
  footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
  }
</style>
